import { createWorkflow } from '../contexts/workflow'
import { type WorkflowSteps } from '../contexts/workflow/types'
import { ProductEnum, UserTypeEnum } from '../generated/graphql'

import { type Quotation } from './context'

import client from '@olino/auth-client/js'

type Path =
  | 'bienvenue'
  | 'company'
  | 'company-details'
  | 'turnover'
  | 'personal-informations'
  | 'produits-professionnel'
  | 'activity'
  | 'heyflow'
  | 'ecommerce-turnover'
  | 'mrp-building-address'
  | 'mrp-building-information'
  | 'mrp-building-info-address'
  | 'loading'
  | 'tailored--cover-date'
  | 'tailored--book-meeting'
  | 'tailored--finalization'
  | 'pricing-rcp'
  | 'pricing-ecomm'
  | 'pricing-mrp'
  | 'termination'
  | 'start-date'
  | 'summary'
  | 'authenticate'
  | 'otp'
  | 'checkout'
  | 'signature'
  | 'success'
  | 'adresse-logement'
  | 'prix-asd'
  | 'user-type'
  | 'produits-particulier'
  | 'adresse-entreprise'
  | 'prix-asdPro'
  | 'checkoutAcheel'
  // | 'taille-entreprise'
  | 'utilisateur-pci'
  | 'produits-collectivite'
  | 'taille-collectivite'
  | 'collectivite-details'
  | 'prix-mre'
  | 'finalization'

const isContactFilled = (q?: Quotation) => {
  const contact = q?.details.contact

  return (
    contact?.email != null &&
    contact?.firstName != null &&
    contact?.lastName != null &&
    contact?.phone != null &&
    contact?.marketingOptIn != null
  )
}

const steps: WorkflowSteps<Path, Quotation> = {
  bienvenue: {
    completed: false,
    goto: 'company',
    //   (q) => {
    //   if (q?.details.companyTypeName === CompanyTypeEnum.PilliotAssurances) {
    //     return 'user-type'
    //   }
    //   return 'user-type'
    // },
    initial: true,
    hideBackwardButton: true,
  },

  'user-type': {
    completed: false,
    goto: (q) => {
      if (q?.details.contact?.userTypeName === UserTypeEnum.Particulier) {
        return 'produits-particulier'
      } else {
        return 'company'
      }
    },
  },

  'utilisateur-pci': {
    completed: false,
    goto: () => 'company',
  },

  turnover: {
    completed: false,
    goto: 'activity',
  },

  'produits-professionnel': {
    completed: false,
    goto: (q) => {
      if (
        q?.details.products?.some(
          (p) =>
            p === ProductEnum.PublicLiability ||
            p === ProductEnum.PublicLiabilityEcomm ||
            p === ProductEnum.MultiRisk ||
            p === ProductEnum.MreAcheel
        )
      ) {
        return 'turnover'
      }
      if (q?.details.products?.some((p) => p === ProductEnum.AssistanceDomestiqueProfessionnel)) {
        return 'adresse-entreprise'
      }
      return 'heyflow'
    },
  },

  'produits-collectivite': {
    completed: false,
    goto: 'taille-collectivite',
  },

  // 'taille-entreprise': {
  //   completed: false,
  //   goto: 'activity',
  // },

  'taille-collectivite': {
    completed: false,
    goto: 'activity',
  },

  'produits-particulier': {
    completed: false,
    goto: (q) => {
      if (q?.details.products?.some((p) => p === ProductEnum.AssistanceDomestique)) {
        return 'adresse-logement'
      }
      return 'produits-particulier'
    },
  },

  activity: {
    completed: (q) =>
      !!q?.details.multiRisk?.activityId ||
      !!q?.details.mreAcheel?.activityId ||
      !!((q?.details.publicLiability?.activityIds?.length || 0) > 0),
    goto: (q) => {
      if (q?.details.products?.some((p: ProductEnum) => p === ProductEnum.PublicLiabilityEcomm)) {
        return 'ecommerce-turnover'
      }

      if (q?.details.products?.some((p: ProductEnum) => p === ProductEnum.MultiRisk)) {
        return 'mrp-building-address'
      }

      if (q?.details.products?.some((p: ProductEnum) => p === ProductEnum.MreAcheel)) {
        return 'mrp-building-info-address'
      }

      return isContactFilled(q) ? 'loading' : 'personal-informations'
    },
  },

  heyflow: {
    completed: false,
    goto: (q) => (isContactFilled(q) ? 'loading' : 'personal-informations'),
    hideBackwardButton: true,
  },

  'adresse-logement': {
    completed: (q) => q?.details.billingAddress != null,
    goto: (q) => (isContactFilled(q) ? 'loading' : 'personal-informations'),
  },

  'adresse-entreprise': {
    completed: (q) => q?.details.billingAddress != null,
    goto: (q) => (isContactFilled(q) ? 'loading' : 'personal-informations'),
  },

  'ecommerce-turnover': {
    completed: false,
    goto: (q) => (isContactFilled(q) ? 'loading' : 'personal-informations'),
  },

  'mrp-building-address': {
    completed: (q) => q?.details.multiRisk?.address != null,
    goto: () => 'mrp-building-information',
  },

  'mrp-building-info-address': {
    completed: (q) =>
      q?.details.mreAcheel?.historicalMonument != null || q?.details.mreAcheel?.surfaceArea != null,
    goto: (q) => (isContactFilled(q) ? 'loading' : 'personal-informations'),
  },

  'mrp-building-information': {
    completed: (q) =>
      q?.details.multiRisk?.contentValue != null ||
      q?.details.multiRisk?.historicalMonument != null ||
      q?.details.multiRisk?.occupancyType != null ||
      q?.details.multiRisk?.surfaceArea != null,
    goto: (q) => (isContactFilled(q) ? 'loading' : 'personal-informations'),
  },

  'personal-informations': {
    completed: false,
    goto: 'loading',
  },

  loading: {
    completed: false,
    hideBackward: true,
    hideBackwardButton: true,
    goto: (q) => {
      if (q?.details.products?.includes(ProductEnum.MultiRisk) && q?.premium?.multiRisk) {
        return 'pricing-mrp'
      }

      if (q?.details.products?.includes(ProductEnum.MreAcheel) && q?.premium?.mreAcheel) {
        return 'prix-mre'
      }

      if (
        q?.details.products?.includes(ProductEnum.PublicLiability) &&
        q?.premium?.publicLiability
      ) {
        return 'pricing-rcp'
      }

      if (
        q?.details.products?.includes(ProductEnum.PublicLiabilityEcomm) &&
        q?.premium?.publicLiabilityEcomm
      ) {
        return 'pricing-ecomm'
      }

      if (
        q?.details.products?.includes(ProductEnum.AssistanceDomestique) &&
        q?.premium?.assistanceDomestique
      ) {
        return 'prix-asd'
      }

      if (
        q?.details.products?.includes(ProductEnum.AssistanceDomestiqueProfessionnel) &&
        q?.premium?.assistanceDomestiqueProfessionnel
      ) {
        return 'prix-asdPro'
      }

      if (
        q?.details.products?.includes(ProductEnum.MreAcheel) &&
        q?.details?.mreAcheel?.procedureCollective
      ) {
        return 'finalization'
      }

      return 'tailored--finalization'
    },
  },

  'tailored--cover-date': {
    completed: false,
    goto: () => 'tailored--finalization',
  },

  'tailored--book-meeting': {
    completed: false,
    goto: () => 'tailored--finalization',
  },

  'tailored--finalization': {
    completed: false,
  },

  finalization: {
    completed: false,
  },

  'pricing-rcp': {
    completed: false,
    goto: () => 'start-date',
  },

  'pricing-ecomm': {
    completed: false,
    goto: () => 'start-date',
  },

  'pricing-mrp': {
    completed: false,
    goto: () => 'start-date',
  },

  'prix-mre': {
    completed: false,
    goto: () => 'start-date',
  },

  termination: {
    completed: false,
    goto: () => 'start-date',
  },

  'prix-asd': {
    completed: false,
    goto: () => 'start-date',
  },

  'prix-asdPro': {
    completed: false,
    goto: () => 'start-date',
  },

  'start-date': {
    completed: false,
    goto: () => {
      return 'summary'
    },
  },

  company: {
    completed: false,
    goto: (q) => {
      // if (q?.details?.contact?.userTypeName === UserTypeEnum.Professionnel) {
      return 'company-details'
      // } else {
      //   return 'collectivite-details'
      // }
    },
  },

  'collectivite-details': {
    completed: false,
    goto: 'produits-collectivite',
  },

  'company-details': {
    completed: false,
    goto: 'produits-professionnel',
  },

  summary: {
    completed: false,
    goto: (q) => {
      const currentUser = client.getCurrentUser()

      // if (
      //   q?.details?.contact?.email &&
      //   currentUser?.email &&
      //   q?.details?.contact?.email === currentUser?.email &&
      //   q?.details.products?.some((p) => p === ProductEnum.MreAcheel)
      // )
      //   return 'checkoutAcheel'
      // else if (
      if (
        q?.details?.contact?.email &&
        currentUser?.email &&
        q?.details?.contact?.email === currentUser?.email
      )
        return 'checkout'

      return 'otp'
    },
  },

  authenticate: {
    completed: false,
    goto: () => 'otp',
  },

  otp: {
    completed: false,
    hideBackward: true,
    goto: () => 'checkout',
  },

  checkout: {
    completed: false,
    goto: () => 'signature',
  },

  checkoutAcheel: {
    completed: false,
    goto: () => 'signature',
  },

  signature: {
    completed: false,
    hideBackwardButton: true,
    goto: () => 'success',
  },

  success: {
    completed: false,
  },
}

const { provider, useContext } = createWorkflow<Path, Quotation>()

export {
  Path as OnboardingWorkflowPath,
  steps as OnboardingWorkflowSteps,
  provider as OnboardingWorkflowProvider,
  useContext as useOnboardingWorkflow,
}
